import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Send, History, FileText, Settings as SettingsIcon, Plus, Edit, Trash2, Copy, X, Save, Loader, CheckCircle, AlertCircle, Users, RefreshCw } from 'lucide-react';
import { NotificationService } from '../../services/notificationService';

const Notifications = () => {
  const [activeTab, setActiveTab] = useState('send');
  const [showSendModal, setShowSendModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('system');
  const [selectedTargetRole, setSelectedTargetRole] = useState('all');

  const [templateTitle, setTemplateTitle] = useState('');
  const [templateMessage, setTemplateMessage] = useState('');
  const [templateCategory, setTemplateCategory] = useState('system');
  const [templateTargetRole, setTemplateTargetRole] = useState('all');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
    } catch { return {}; }
  })();

  const categories = [
    { id: 'system', name: 'System', color: '#059669' },
    { id: 'service_request', name: 'Service Request', color: '#f59e0b' },
    { id: 'payment', name: 'Payment', color: '#ef4444' },
    { id: 'reminder', name: 'Reminder', color: '#8b5cf6' },
    { id: 'chat', name: 'Chat', color: '#3b82f6' },
    { id: 'shipment', name: 'Shipment', color: '#0891b2' },
    { id: 'order', name: 'Order', color: '#d97706' },
    { id: 'delivery', name: 'Delivery', color: '#16a34a' },
    { id: 'booking', name: 'Booking', color: '#7c3aed' },
  ];

  const targetRoles = [
    { id: 'all', name: 'All Users' },
    { id: 'customer', name: 'Customers' },
    { id: 'mechanic', name: 'Mechanics' },
    { id: 'telecaller', name: 'Telecallers' },
    { id: 'serviceexecutive', name: 'Service Executives' },
    { id: 'serviceadvisor', name: 'Service Advisors' },
    { id: 'teamlead', name: 'Team Leads' },
    { id: 'admin', name: 'Admins' },
  ];

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await NotificationService.getBroadcastHistory(50, 0);
      setBroadcastHistory(data);
    } catch (e) {
      console.error('Failed to load history', e);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const data = await NotificationService.listTemplates();
      setTemplates(data);
    } catch (e) {
      console.error('Failed to load templates', e);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
    loadTemplates();
  }, [loadHistory, loadTemplates]);

  const handleSendNotification = async () => {
    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      setSendResult({ type: 'error', message: 'Title aur message dono required hain' });
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const result = await NotificationService.sendBroadcast({
        title: notificationTitle.trim(),
        message: notificationMessage.trim(),
        category: selectedCategory,
        targetRole: selectedTargetRole,
        sentBy: currentUser?.id || null,
      });

      if (result.success) {
        setSendResult({ type: 'success', message: `Notification ${result.sent} users ko bheji gayi!` });
        setNotificationTitle('');
        setNotificationMessage('');
        setSelectedCategory('system');
        setSelectedTargetRole('all');
        setTimeout(() => {
          setShowSendModal(false);
          setSendResult(null);
        }, 2000);
        loadHistory();
      } else {
        setSendResult({ type: 'error', message: result.error || 'Send karne mein error aaya' });
      }
    } catch (e) {
      setSendResult({ type: 'error', message: e.message || 'Send karne mein error aaya' });
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateTitle.trim() || !templateMessage.trim()) return;
    setSavingTemplate(true);
    try {
      const payload = {
        title: templateTitle.trim(),
        message: templateMessage.trim(),
        category: templateCategory,
        targetRole: templateTargetRole,
      };
      if (editingTemplate) payload.id = editingTemplate.id;

      const result = await NotificationService.upsertTemplate(payload);
      if (result) {
        setShowTemplateModal(false);
        setTemplateTitle('');
        setTemplateMessage('');
        setEditingTemplate(null);
        loadTemplates();
      }
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Kya aap ye template delete karna chahte hain?')) return;
    await NotificationService.deleteTemplate(id);
    loadTemplates();
  };

  const useTemplate = (template) => {
    setNotificationTitle(template.title);
    setNotificationMessage(template.message);
    setSelectedCategory(template.category || 'system');
    setSelectedTargetRole(template.target_role || 'all');
    setShowSendModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getCategoryStyle = (cat) => {
    const c = categories.find(x => x.id === cat);
    if (!c) return { backgroundColor: '#f3f4f6', color: '#6b7280' };
    return { backgroundColor: c.color + '15', color: c.color };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Notification Manager</h1>
          <p className="text-gray-600 mt-1">Send and manage system notifications</p>
        </div>
        <button
          onClick={() => setShowSendModal(true)}
          className="flex items-center space-x-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 shadow-sm"
        >
          <Send size={18} />
          <span>Send Notification</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-8">
          {[
            { id: 'send', label: 'Send', icon: Send },
            { id: 'history', label: 'History', icon: History },
            { id: 'templates', label: 'Templates', icon: FileText },
            { id: 'preferences', label: 'Preferences', icon: SettingsIcon },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 pb-4 border-b-2 transition ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon size={20} />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Send Tab */}
      {activeTab === 'send' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setShowSendModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                <Send size={20} />
                <span>Send Notification</span>
              </button>
              <button
                onClick={() => setActiveTab('templates')}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <FileText size={20} />
                <span>Use Template</span>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
              <button onClick={loadHistory} className="text-gray-400 hover:text-gray-600">
                <RefreshCw size={18} />
              </button>
            </div>
            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <Loader size={24} className="animate-spin text-primary-500" />
              </div>
            ) : broadcastHistory.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Abhi tak koi notification nahi bheji gayi</p>
            ) : (
              <div className="space-y-3">
                {broadcastHistory.slice(0, 5).map(item => (
                  <div key={item.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-2.5 h-2.5 bg-green-500 rounded-full flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                      <p className="text-xs text-gray-500 truncate">{item.message}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-medium text-primary-600">{item.recipient_count} users</p>
                      <p className="text-xs text-gray-400">{formatDate(item.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Broadcast History</h2>
            <button
              onClick={loadHistory}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
            >
              <RefreshCw size={14} />
              <span>Refresh</span>
            </button>
          </div>

          {loadingHistory ? (
            <div className="flex justify-center py-12">
              <Loader size={28} className="animate-spin text-primary-500" />
            </div>
          ) : broadcastHistory.length === 0 ? (
            <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
              <Bell size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">Koi broadcast history nahi hai</p>
              <p className="text-gray-400 text-sm mt-1">Pehli notification bhejne ke baad yahan dikhegi</p>
            </div>
          ) : (
            broadcastHistory.map(item => (
              <div key={item.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full" style={getCategoryStyle(item.category)}>
                    {item.category}
                  </span>
                </div>
                <p className="text-gray-600 mb-4">{item.message}</p>
                <div className="flex items-center flex-wrap gap-4 text-sm text-gray-500">
                  <span className="flex items-center space-x-1">
                    <Users size={14} />
                    <span>{item.recipient_count} recipients</span>
                  </span>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">
                    Target: {targetRoles.find(r => r.id === item.target_role)?.name || item.target_role}
                  </span>
                  <span>{formatDate(item.created_at)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Notification Templates</h2>
            <div className="flex space-x-2">
              <button onClick={loadTemplates} className="p-2 text-gray-400 hover:text-gray-600">
                <RefreshCw size={18} />
              </button>
              <button
                onClick={() => {
                  setEditingTemplate(null);
                  setTemplateTitle('');
                  setTemplateMessage('');
                  setTemplateCategory('system');
                  setTemplateTargetRole('all');
                  setShowTemplateModal(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                <Plus size={20} />
                <span>New Template</span>
              </button>
            </div>
          </div>

          {loadingTemplates ? (
            <div className="flex justify-center py-12">
              <Loader size={28} className="animate-spin text-primary-500" />
            </div>
          ) : templates.length === 0 ? (
            <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
              <FileText size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">Koi template nahi hai</p>
              <p className="text-gray-400 text-sm mt-1">Naya template banayein taaki notifications jaldi bhej sakein</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map(template => (
                <div key={template.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{template.title}</h3>
                    <span className="px-2 py-1 text-xs rounded-full" style={getCategoryStyle(template.category)}>
                      {template.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">{template.message}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      Target: {targetRoles.find(r => r.id === template.target_role)?.name || template.target_role}
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => useTemplate(template)}
                        className="px-3 py-1 text-xs bg-primary-50 text-primary-700 rounded hover:bg-primary-100"
                      >
                        Use
                      </button>
                      <button
                        onClick={() => {
                          setEditingTemplate(template);
                          setTemplateTitle(template.title);
                          setTemplateMessage(template.message);
                          setTemplateCategory(template.category || 'system');
                          setTemplateTargetRole(template.target_role || 'all');
                          setShowTemplateModal(true);
                        }}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        <Edit size={14} className="inline" />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        <Trash2 size={14} className="inline" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Notification Categories</h2>
          <p className="text-gray-500 mb-6">Ye categories notifications bhejte waqt use hoti hain</p>
          <div className="space-y-4">
            {categories.map(cat => (
              <div key={cat.id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: cat.color }}></div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{cat.name}</p>
                  <p className="text-sm text-gray-500">Category ID: {cat.id}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Send Notification Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Send Notification</h2>
              <button onClick={() => { setShowSendModal(false); setSendResult(null); }} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            {sendResult && (
              <div className={`mb-4 p-4 rounded-lg flex items-center space-x-3 ${
                sendResult.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {sendResult.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                <span className="font-medium">{sendResult.message}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Notification title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea
                  value={notificationMessage}
                  onChange={(e) => setNotificationMessage(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Notification message"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
                  <select
                    value={selectedTargetRole}
                    onChange={(e) => setSelectedTargetRole(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {targetRoles.map(role => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => { setShowSendModal(false); setSendResult(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendNotification}
                  disabled={sending}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center space-x-2 disabled:opacity-50"
                >
                  {sending ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      <span>Send Notification</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </h2>
              <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={templateTitle}
                  onChange={(e) => setTemplateTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Template title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea
                  value={templateMessage}
                  onChange={(e) => setTemplateMessage(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Template message"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={templateCategory}
                    onChange={(e) => setTemplateCategory(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
                  <select
                    value={templateTargetRole}
                    onChange={(e) => setTemplateTargetRole(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {targetRoles.map(role => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={savingTemplate}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={savingTemplate}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center space-x-2 disabled:opacity-50"
                >
                  {savingTemplate ? (
                    <Loader size={18} className="animate-spin" />
                  ) : (
                    <Save size={18} />
                  )}
                  <span>{editingTemplate ? 'Update' : 'Save'} Template</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
